import React, { useEffect, useRef } from 'react';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import useFilterTableState from 'talent_matching/hooks/use_filter_table_state';
import useStickyGridHeader from 'talent_matching/hooks/use_sticky_grid_header';
import useSidePanel from 'talent_matching/hooks/use_side_panel';
import { useMyGreenhouseCandidates } from 'talent_matching/my_greenhouse/hooks/use_my_greenhouse_candidates';
import { useMyGreenhouseColumns } from 'talent_matching/my_greenhouse/components/table/my_greenhouse_columns';
import { useMyGreenhouseTabContext } from 'talent_matching/contexts/my_greenhouse_tab_context';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';
import BaseDataTable from 'talent_matching/components/shared/base_data_table';
import NoOverflowFullWidth from 'talent_matching/components/shared/no_overflow_full_width';
import MyGreenhouseSidePanel from 'talent_matching/components/side_panel/my_greenhouse_side_panel';
import { TABS } from 'talent_matching/contexts/user_context';
import { Person } from 'talent_matching/types';
import { RowDef } from '@grnhse/seedling/lib/birch/components/table/types';
import NoResultsOverlay from 'talent_matching/components/shared/no_results_overlay';
import { withLocalePrefix } from 'shared/utils/translation';
import FingerprintOne from '@grnhse/seedling/lib/birch/illustrations/fingerprint_one';
import { EmptyState } from '@grnhse/seedling/lib/birch/components/empty_state';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';

const t = withLocalePrefix('talent_matching.my_greenhouse.empty_state');

type Props = {
  onTotalRowsChange?: (totalRows: number) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  hasAnyFilters?: boolean;
  onClearAll?: () => void;
};

const MyGreenhouseTableWrapper = ({
  onTotalRowsChange,
  columnVisibilityModel,
  setColumnVisibilityModel,
  hasAnyFilters = false,
  onClearAll,
}: Props) => {
  const { paths } = useMyGreenhouseTabContext();
  const { hasCalibrationSetting } = useRankingInProgress();

  const {
    data: tableData,
    isLoading,
    isFetching,
    isPlaceholderData,
  } = useMyGreenhouseCandidates(paths.candidatesPath, {
    enabled: hasCalibrationSetting,
  });

  const rows = tableData?.rows || [];
  const totalRows = tableData?.totalRows ?? 0;

  const { mountKey, currentPage, rowsPerPage, onPageChange } =
    useFilterTableState({ tabId: TABS.MY_GREENHOUSE, totalRows });

  useEffect(() => {
    onTotalRowsChange?.(totalRows);
  }, [totalRows, onTotalRowsChange]);

  const tableLoading = isLoading || isFetching || isPlaceholderData;
  const columns = useMyGreenhouseColumns();

  const { wrapperRef } = useStickyGridHeader({ refreshKey: mountKey });
  const savedColumnWidthsRef = useRef<Record<string, number>>({});

  const setRefs = (el: HTMLDivElement | null) => {
    wrapperRef.current = el;
  };

  const {
    isPanelOpen,
    setIsPanelOpen,
    selectedRow,
    selectedRowIndex,
    popoverRef,
    previousRow,
    nextRow,
    handlePanelToggle,
    handleRowClick,
    getRowClassName,
  } = useSidePanel({ rows });

  if (!hasCalibrationSetting) {
    return (
      <NoOverflowFullWidth>
        <Stack sx={{ marginTop: 5 }}>
          <EmptyState
            header={t('no_calibration_header')}
            body={t('no_calibration_body')}
            Illustration={FingerprintOne}
            size="sm"
          />
        </Stack>
      </NoOverflowFullWidth>
    );
  }

  return (
    <NoOverflowFullWidth>
      <div ref={setRefs}>
        <BaseDataTable
          key={mountKey}
          rows={rows}
          columns={columns}
          totalRows={totalRows}
          loading={tableLoading}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={onPageChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          savedColumnWidthsRef={savedColumnWidthsRef}
          slots={{
            noRowsOverlay: () => (
              <NoResultsOverlay
                hasAnyFilters={hasAnyFilters}
                onClearAll={onClearAll}
                filteredHeader={t('no_results_header')}
                filteredBody={t('no_results_body')}
                clearButtonLabel={t('clear_all_filters')}
                emptyHeader={t('no_candidates_header')}
                emptyBody={t('no_candidates_body')}
              />
            ),
          }}
          onRowClick={(row: RowDef) => handleRowClick(row as Person)}
          getRowClassName={getRowClassName}
        />
      </div>

      {selectedRow && (
        <MyGreenhouseSidePanel
          ref={popoverRef}
          applicationId={selectedRow.id}
          onClose={() => setIsPanelOpen(false)}
          onToggle={handlePanelToggle}
          isVisible={isPanelOpen}
          rowCount={rows.length}
          currentIndex={selectedRowIndex}
          previousCandidate={previousRow}
          nextCandidate={nextRow}
          selectCandidate={handleRowClick}
        />
      )}
    </NoOverflowFullWidth>
  );
};

export default MyGreenhouseTableWrapper;
